import { useData } from "../contexts/DataContext/DataContextt";
import { useCallback } from "react";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Button,
  Chip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import TabulatedGridCollapsibleV2 from "../components/Forms/TabulatedGridCollapsibleV2";
import { mergePipeStrings } from "../helpers/EntityHelper";
import PageContainer from "../components/Forms/PageContainer";
import { useNavigate } from "react-router";
import { deleteFeature, fetchFeatures } from "../services/featureService";
import { featuresPageCache } from "./cache/PageCache";
import { usePagedQuery } from "../contexts/hooks/usePagedQuery";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { RowActions } from "../utils/RowActions";
import { DataType } from "../types/constant";

const typeColors = {
  [DataType.ALPHANUMERIC]: "warning",
  [DataType.NUMERIC]: "info",
  [DataType.RANGE]: "secondary",
  [DataType.LOGICAL]: "success",
};

let FeaturePageNew = ({ tableStyle, rowActions, pageStyle = {} }) => {
  const { language } = useData();
  const { showPageContainerTools = true } = pageStyle;
  const transform = useCallback(
    (res) => ({
      ...res,
      results: res.results?.map((m) => ({
        ...m,
        selectedDefaultUnit:
          mergePipeStrings(
            m.defaultUnit?.map((v) => v.unitCode),
            [],
          ) ?? "",
        selectedDefaultValues:
          mergePipeStrings(
            m.defaultValues?.map((v) => v.valueCode),
            [],
          ) ?? "",
        values: m.defaultValues,
        unit: m.defaultUnit,
      })),
    }),
    [],
  );
  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    handleKeyDown,
    handleSearch,
    data: featureData,
    total,
    searchTerm,
    isLoading,
    reload,
    handleDeleteAction,
    searchType,
    setSearchType,
    searchOption,
  } = usePagedQuery({
    fetchFunction: fetchFeatures,
    searchOption: [
      { id: "featureId", display: "Code" },
      { id: "featureName", display: "Name" },
    ],
    searchKey: "",
    cache: featuresPageCache,
    language,
    deps: [language],
    transform: transform,
  });
  const navigate = useNavigate();

  const pageRowActions = [
    {
      tooltip: "Edit Feature",
      icon: <EditNoteRoundedIcon />,
      onClick: (row) =>
        navigate(`/feature/edit/${row.featureId}`, { state: { row } }),
    },
    {
      tooltip: "Delete Feature",
      icon: <DeleteForeverRoundedIcon />,
      color: "error",
      onClick: (row) => {
        handleDeleteAction(deleteFeature, row, "featureId", {
          title: "Delete Feature",
          message: `Are you sure you want to delete this feature? ${row.featureId}`,
        });
      },
    },
  ];

  const handleCreateClick = useCallback(() => {
    navigate(`/features/new`);
  }, [navigate]);

  const headers = [
    {
      prop: "featureId",
      text: "Code",
      width: 150,
      link: {
        action: (row) =>
          navigate(`/feature/edit/${row.featureId}`, { state: { row } }),
      },
    },
    { prop: "featureName", text: "Name" },
    { prop: "featureDisplayName", text: "Display Name" },
    {
      prop: "featureType",
      text: "Type",
      width: 140,
      format: (value) =>
        value ? (
          <Chip
            size="small"
            variant="outlined"
            label={value}
            color={typeColors[value] ?? "default"}
          />
        ) : null,
    },
    {
      prop: "featureImage",
      text: "Symbols",
      width: 150,
      isImage: true,
      align: "center",
    },
  ];

  return (
    <PageContainer
      title="ETIM Features"
      {...(showPageContainerTools && {
        subtitle: "Manage ETIM features, their types, units and values",
        titleAdornment: (
          <Chip
            size="small"
            label={total}
            aria-label={`${total} ${searchTerm ? "matching " : ""}features`}
          />
        ),
        breadcrumbs: [{ title: "ETIM Features" }],
        actions: (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip
              title="Refresh Etim Feature"
              placement="bottom"
              enterDelay={300}
            >
              <div>
                <IconButton
                  size="small"
                  aria-label="refresh"
                  onClick={() => reload(true)}
                >
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>

            <Button
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          </Stack>
        ),
      })}
    >
      <Box mt={3}>
        <TabulatedGridCollapsibleV2
          variant="modern"
          loading={isLoading}
          emptyState={
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 8, color: "text.secondary" }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography>No features found</Typography>
              {searchTerm && (
                <Button size="small" onClick={() => handleSearch("")}>
                  Clear search
                </Button>
              )}
            </Stack>
          }
          pagination={{
            totalRows: total,
            page: page,
            rowsPerPage: rowsPerPage,
            handleChangePage: handleChangePage,
            handleChangeRowsPerPage: handleChangeRowsPerPage,
          }}
          search={{
            placeHolder: "Search...",
            searchOption: searchOption,
            searchType: searchType,
            setSearchType: setSearchType,
            handleSearchClick: handleSearch,
            onKeyDown: handleKeyDown,
            activeTerm: searchTerm,
            onClear: () => handleSearch(""),
          }}
          data={featureData}
          showHeader={true}
          headers={headers}
          rowActions={(row) => (
            <RowActions row={row} actions={pageRowActions} />
          )}
        />
      </Box>
    </PageContainer>
  );
};

export default FeaturePageNew;
